/**
 * Public accept-invite screen (PRE-AUTH). Reached at /accept-invite?token=… from an invite email and
 * rendered by App() ABOVE the AuthGate, so it runs OUTSIDE AuthProvider/RbacProvider — it owns its own
 * Auth0 context. Flow: read ?token= → api.validateInvite (anonymous) → show "sign in as {email}". The
 * primary action starts an Auth0 login PREFILLED with the invitee's email (login_hint) and lands them
 * back at "/" authenticated, where the normal roster gate takes over.
 *
 * Visual style mirrors SignIn.tsx (brand "Z" card, bg-(--…) tokens — Tailwind v4 paren syntax).
 *
 * In MOCK / DEV-BYPASS mode there is no Auth0 tenant, so the sign-in action degrades to a plain
 * navigation to "/" (where the mock session is injected) instead of wiring an Auth0Provider that would
 * throw on the missing config.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/ui";
import { api, ApiRequestError } from "@/api/client";
import { USE_MOCK_API, DEV_BYPASS_AUTH, getAuth0Config } from "@/lib/env";

type State =
  | { kind: "validating" }
  | { kind: "valid"; email: string }
  | { kind: "problem" } // transient: network / 5xx / timeout → retryable, NOT a dead token
  | { kind: "invalid"; reason: "missingToken" | "expired" };

const VALIDATE_TIMEOUT_MS = 12_000;

/** A transient failure (offline / DNS / CORS = status 0, any 5xx, or a timeout-abort that carries no
 *  status) is retryable and must NOT be shown as an expired invite; a 4xx means a genuinely dead or
 *  unknown token. Keeps a valid invitee from being told to give up over a momentary blip. */
function isTransient(err: unknown): boolean {
  const status = err instanceof ApiRequestError ? err.error.status : undefined;
  return status === undefined || status === 0 || status >= 500;
}

/** Read + trim the ?token= param. Returns null for a missing/blank token (→ missingToken state). */
function readToken(): string | null {
  if (typeof window === "undefined") return null;
  const token = new URLSearchParams(window.location.search).get("token");
  return token && token.trim() ? token : null;
}

/** The brand mark shared with SignIn — a small square "Z" tile. */
function BrandMark() {
  return (
    <span
      className="flex size-12 items-center justify-center rounded-(--radius) bg-(--brand) text-(--text-onbrand) shadow-(--shadow-sm)"
      aria-hidden
    >
      <span className="text-(length:--text-lg) font-bold tracking-tight">Z</span>
    </span>
  );
}

export function AcceptInvite() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>(() =>
    readToken() ? { kind: "validating" } : { kind: "invalid", reason: "missingToken" },
  );
  const [attempt, setAttempt] = useState(0); // bump to re-validate after a transient failure

  useEffect(() => {
    const token = readToken();
    if (!token) return; // already in the missingToken state
    setState({ kind: "validating" });
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), VALIDATE_TIMEOUT_MS); // never hang on a silent stall
    api
      .validateInvite(token, ctrl.signal)
      .then((res) => {
        if (!cancelled) setState({ kind: "valid", email: res.email });
      })
      .catch((err: unknown) => {
        // A dead / unknown token (4xx) → a non-leaky invalid state; a transient failure (network / 5xx
        // / timeout) → a retryable "problem" state, so a valid invitee isn't misdirected into giving up.
        if (!cancelled) {
          setState(isTransient(err) ? { kind: "problem" } : { kind: "invalid", reason: "expired" });
        }
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [attempt]);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-(--bg) px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-7 rounded-(--radius-lg) border border-(--border) bg-(--surface) px-8 py-10 text-center shadow-(--shadow-lg)">
        <BrandMark />
        {state.kind === "validating" && (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            {t("acceptInvite.validating")}
          </p>
        )}

        {state.kind === "problem" && (
          <>
            <div className="flex flex-col gap-1.5">
              <h1 className="text-(length:--text-xl) font-semibold text-(--text-strong)">
                {t("state.errorTitle")}
              </h1>
              <p className="text-(length:--text-sm) text-(--text-muted)">{t("state.errorBody")}</p>
            </div>
            <Button size="lg" className="w-full" onClick={() => setAttempt((a) => a + 1)}>
              {t("action.retry")}
            </Button>
          </>
        )}

        {state.kind === "valid" && (
          <>
            <div className="flex flex-col gap-1.5">
              <h1 className="text-(length:--text-xl) font-semibold text-(--text-strong)">
                {t("acceptInvite.title")}
              </h1>
              <p className="text-(length:--text-sm) text-(--text-muted)">
                {t("acceptInvite.validBody", { email: state.email })}
              </p>
            </div>
            <SignInAction email={state.email} />
            <p className="text-(length:--text-2xs) text-(--text-subtle)">{t("auth.staffOnly")}</p>
          </>
        )}

        {state.kind === "invalid" && (
          <div className="flex flex-col gap-1.5">
            <h1 className="text-(length:--text-xl) font-semibold text-(--text-strong)">
              {t("acceptInvite.invalidTitle")}
            </h1>
            <p className="text-(length:--text-sm) text-(--text-muted)">
              {state.reason === "missingToken"
                ? t("acceptInvite.missingToken")
                : t("acceptInvite.invalidBody")}
            </p>
          </div>
        )}
      </div>
    </main>
  );
}

/**
 * The primary action. Prefilling the login needs Auth0 context, so in REAL mode we wrap our OWN
 * Auth0Provider here (reusing getAuth0Config — the same config the app-wide AuthProvider uses). In
 * MOCK / DEV-BYPASS mode there is no tenant, so we render a plain button that just navigates to "/".
 */
function SignInAction({ email }: { email: string }) {
  const { t } = useTranslation();

  if (USE_MOCK_API || DEV_BYPASS_AUTH) {
    return (
      <Button size="lg" className="w-full" onClick={() => window.location.assign("/")}>
        {t("acceptInvite.signIn")}
      </Button>
    );
  }

  const { domain, clientId, audience } = getAuth0Config();
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{ redirect_uri: window.location.origin, audience }}
      cacheLocation="localstorage"
      useRefreshTokens
    >
      <LoginHintButton email={email} />
    </Auth0Provider>
  );
}

/** Inner button (inside Auth0Provider) — starts the redirect login prefilled with the invitee email. */
function LoginHintButton({ email }: { email: string }) {
  const { t } = useTranslation();
  const { loginWithRedirect } = useAuth0();
  return (
    <Button
      size="lg"
      className="w-full"
      onClick={() => void loginWithRedirect({ authorizationParams: { login_hint: email } })}
    >
      {t("acceptInvite.signIn")}
    </Button>
  );
}
